import { useEffect } from 'react'

/**
 * Prefetch dei chunk delle pagine principali subito dopo il primo render del
 * layout autenticato. Senza questo, la prima visita a ciascuna route paga
 * 800ms-2s di download+parse del bundle (Vite splitta per route). Con
 * `requestIdleCallback` lo scarichiamo in background appena il browser è
 * idle, così la successiva navigazione è istantanea.
 *
 * Le import() qui sotto sono "stringhe vive": Vite/Rollup le risolve a
 * compile-time per generare i chunk hashati corrispondenti. Vanno tenute in
 * sync coi nomi importati da `router.tsx` — un mismatch genera due chunk
 * separati anziché uno cachato.
 */
const ROUTES_TO_PREFETCH = [
  () => import('@/pages/dashboard/overview'),
  () => import('@/pages/clients/clients-list'),
  () => import('@/pages/cases/cases-list'),
  () => import('@/pages/deadlines/deadlines-list'),
  () => import('@/pages/billing/wallet'),
  () => import('@/pages/studio/profile'),
  () => import('@/pages/studio/team'),
  () => import('@/pages/settings/account'),
]

type IdleCb = (cb: () => void) => number
const requestIdle: IdleCb =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? (cb) => (window as unknown as { requestIdleCallback: IdleCb }).requestIdleCallback(cb)
    : (cb) => window.setTimeout(cb, 1000)

let prefetchStarted = false

export function usePrefetchRoutes() {
  useEffect(() => {
    if (prefetchStarted) return
    prefetchStarted = true
    // Sequenziale per non saturare la rete dell'utente sulla prima pagina:
    // un chunk alla volta, ognuno scaricato nel successivo idle slot.
    let i = 0
    const step = () => {
      if (i >= ROUTES_TO_PREFETCH.length) return
      const loader = ROUTES_TO_PREFETCH[i++]!
      loader()
        .catch(() => {
          // Network/stale-chunk failure: silenzioso, RouteErrorBoundary
          // gestisce comunque il reload alla navigazione effettiva.
        })
        .finally(() => requestIdle(step))
    }
    requestIdle(step)
  }, [])
}
